import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, CheckCircle, Clock, Target, Calendar } from 'lucide-react';

function TractionSection({ content }) {
  const current = [
    'Complete technical architecture documented',
    'Design system finalized (Tamagui)',
    '132+ pages of product documentation',
    'Domain secured (KoreFolio.com)',
    'AI-assisted development pipeline ready',
  ];

  const milestones = [
    { day: 'Day 10', goal: 'Users can register and create profiles', status: 'pending' },
    { day: 'Day 25', goal: 'Job Connect core complete with instant notifications', status: 'pending' },
    { day: 'Day 35', goal: 'KoreCode sharing system and viral loop enabled', status: 'pending' },
    { day: 'Day 45', goal: 'MVP complete, ready for soft launch', status: 'pending' },
  ];

  const yearOne = [
    { month: 'Month 3', target: '1,000 professionals registered', metric: '1K' },
    { month: 'Month 6', target: '3,000 professionals, 200 businesses', metric: '3K' },
    { month: 'Month 12', target: '10,000 users, $80K MRR', metric: '10K' },
  ];

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-kore-500/10 border border-kore-500/20 rounded-full mb-6">
            <Rocket className="w-4 h-4 text-kore-400" />
            <span className="text-sm text-kore-400">Traction & Roadmap</span>
          </div>
          <h2 className="section-title mb-4">
            <span className="text-gradient">45 Days</span> to Market
          </h2>
          <p className="section-subtitle mx-auto">
            AI-assisted development enables rapid iteration and delivery.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Current Progress */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-accent-mint" />
              Completed
            </h3>
            <div className="space-y-3">
              {current.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-mint/20 flex items-center justify-center mt-0.5">
                    <CheckCircle className="w-3 h-3 text-accent-mint" />
                  </div>
                  <span className="text-sm text-zinc-300">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* MVP Milestones */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="card"
          >
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-kore-400" />
              MVP Milestones
            </h3>
            <div className="space-y-4">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.day}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="flex-shrink-0 w-16 text-xs font-mono text-kore-400 pt-1">
                    {milestone.day}
                  </div>
                  <div className="flex-1">
                    <div className="w-full h-2 bg-surface-elevated rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-kore-500 to-accent-lavender rounded-full"
                        style={{ width: '0%' }}
                      />
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">{milestone.goal}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Year 1 Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8"
        >
          <div className="card bg-gradient-to-r from-surface-card to-kore-900/20">
            <h3 className="text-lg font-semibold mb-6 flex items-center gap-2 justify-center">
              <Calendar className="w-5 h-5 text-kore-400" />
              Year 1 Targets
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {yearOne.map((item, index) => (
                <motion.div
                  key={item.month}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-sm text-zinc-500 mb-2">{item.month}</div>
                  <div className="text-4xl font-bold text-gradient mb-1">{item.metric}</div>
                  <div className="text-xs text-zinc-400">{item.target}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default TractionSection;
